import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { EndorsementInput } from "@/types";
import { ratingToColumn } from "@/types";
import { MARGIN_X, CONTENT_W, drawHeader } from "./pdf-utils";

export function drawAssessmentPage(doc: jsPDF, input: EndorsementInput): void {
  let y = drawHeader(doc, input.organizationName, input.orgBanner, input.orgLogo);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(input.date, MARGIN_X, y);
  y += 8;

  doc.text("Good day!", MARGIN_X, y);
  y += 7;

  const prefix = "Endorsing herewith candidate ";
  doc.text(prefix, MARGIN_X, y);
  const prefixWidth = doc.getTextWidth(prefix);
  doc.setFont("helvetica", "bold");
  doc.text(input.candidateName, MARGIN_X + prefixWidth, y);
  const nameWidth = doc.getTextWidth(input.candidateName);
  doc.setFont("helvetica", "normal");
  const middle = " for the ";
  doc.text(middle, MARGIN_X + prefixWidth + nameWidth, y);
  const middleWidth = doc.getTextWidth(middle);
  doc.setFont("helvetica", "bold");
  doc.text(input.positionTitle, MARGIN_X + prefixWidth + nameWidth + middleWidth, y);
  const posWidth = doc.getTextWidth(input.positionTitle);
  doc.setFont("helvetica", "normal");
  doc.text(" position.", MARGIN_X + prefixWidth + nameWidth + middleWidth + posWidth, y);
  y += 7;

  const intro = doc.splitTextToSize(input.introParagraph, CONTENT_W);
  doc.text(intro, MARGIN_X, y);
  y += intro.length * 5 + 4;

  doc.text(`Current/Last Salary/Benefits: ${input.currentSalary || "—"}`, MARGIN_X, y);
  y += 5;
  doc.text(`Expected Salary: ${input.expectedSalary || "—"}`, MARGIN_X, y);
  y += 5;
  doc.text(`Availability to Start: ${input.availability || "—"}`, MARGIN_X, y);
  y += 8;

  const rows = [
    ["EDUCATION/LICENSES:", input.educationRow],
    ["RELATED EXPERIENCE:", input.experienceRow],
    ["SKILLS/COMPETENCY:", input.skillsRow],
    ["OVERALL IMPRESSION:", input.overallRow],
  ] as const;

  const ratingCols = rows.map(([, row]) => ratingToColumn(row.rating));
  const body = rows.map(([label, row]) => [label, "", "", "", row.remarks || ""]);

  const drawCheckInCell = (cell: { x: number; y: number; width: number; height: number }) => {
    const cx = cell.x + cell.width / 2;
    const cy = cell.y + Math.min(5, cell.height / 2);
    doc.setLineWidth(0.7);
    doc.setDrawColor(0, 0, 0);
    doc.setLineCap("round");
    doc.setLineJoin("round");
    doc.lines([[1.6, 1.6], [3.8, -4]], cx - 2.7, cy - 0.5);
  };

  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 15;
  const tableToRemarksGap = 6;

  let remarksLines: string[] = [];
  let remarksBlockHeight = 0;
  if (input.recruiterRemarks?.trim()) {
    doc.setFontSize(10);
    remarksLines = doc.splitTextToSize(input.recruiterRemarks.trim(), CONTENT_W);
    remarksBlockHeight = (1 + remarksLines.length) * 5;
  }

  const targetTableEndY = pageHeight - bottomMargin - remarksBlockHeight - (remarksBlockHeight > 0 ? tableToRemarksGap : 0);

  const baseTableOpts: Parameters<typeof autoTable>[1] = {
    startY: y,
    head: [["CRITERIA", "EXCEEDS\nEXPECTATIONS\n(3)", "MEETS\nEXPECTATIONS\n(2)", "BELOW\nEXPECTATIONS\n(1)", "REMARKS"]],
    body,
    margin: { left: MARGIN_X, right: MARGIN_X },
    theme: "grid",
    styles: { fontSize: 8.5, cellPadding: 2.2, valign: "top", lineColor: [80, 80, 80], lineWidth: 0.2, overflow: "linebreak" },
    headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold", halign: "center", fontSize: 7.5, cellPadding: { top: 2, right: 1, bottom: 2, left: 1 } },
    columnStyles: {
      0: { cellWidth: 42, fontStyle: "bold" },
      1: { cellWidth: 24, halign: "center", fontStyle: "bold" },
      2: { cellWidth: 24, halign: "center", fontStyle: "bold" },
      3: { cellWidth: 24, halign: "center", fontStyle: "bold" },
      4: { cellWidth: "auto" },
    },
  };

  const measureDoc = new jsPDF({ unit: "mm", format: "a4" });
  let naturalRow3Height = 0;
  autoTable(measureDoc, {
    ...baseTableOpts,
    didDrawCell: (data) => {
      if (data.section === "body" && data.row.index === 3 && data.column.index === 0) naturalRow3Height = data.cell.height;
    },
  });
  // @ts-expect-error — autotable adds lastAutoTable to doc
  const naturalFinalY = measureDoc.lastAutoTable.finalY as number;
  const expansion = Math.max(0, targetTableEndY - naturalFinalY);
  const minRow3Height = naturalRow3Height + expansion;

  autoTable(doc, {
    ...baseTableOpts,
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === 3) data.cell.styles.minCellHeight = minRow3Height;
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      if (data.column.index < 1 || data.column.index > 3) return;
      const targetCol = ratingCols[data.row.index] + 1;
      if (data.column.index === targetCol) drawCheckInCell(data.cell);
    },
  });

  if (input.recruiterRemarks?.trim()) {
    // @ts-expect-error — autotable adds lastAutoTable to doc
    const tableEndY = doc.lastAutoTable.finalY as number;
    const naturalRemarksTop = tableEndY + tableToRemarksGap;
    const anchoredRemarksTop = pageHeight - bottomMargin - remarksBlockHeight;
    const remarksTop = Math.max(naturalRemarksTop, anchoredRemarksTop);
    const titleY = remarksTop + 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Recruiter Remarks:", MARGIN_X, titleY);
    doc.setFont("helvetica", "normal");
    doc.text(remarksLines, MARGIN_X, titleY + 5);
  }
}
